import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ActionIcon, Collapse, Flex, Text } from '@mantine/core';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon';
import { ActiveJourneyStyles } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Investors/InvestorDetailsV2/components/ActiveJourneysCard/ActiveJourneysCard.styles.tsx';
import {
	getIndicatorColor,
	getIndicatorIcon,
} from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Investors/InvestorDetailsV2/components/ActiveJourneysCard/activeJourneys.utils.ts';
import { ActiveJourney as ActiveJourneyModel } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Investors/InvestorDetailsV2/components/ActiveJourneysCard/activeJourneys.types.ts';
import { makeComplexDataTestId, TEST_ELEMENT_IDS } from '@dealstack/shared/tests/testElementIds';

interface Props {
	journey: ActiveJourneyModel;
}

export const ActiveJourney = ({ journey }: Props) => {
	const [expanded, setExpanded] = useState(false);
	const isExpandable = journey.indicators.length > 0;

	return (
		<ActiveJourneyStyles
			className={expanded ? 'expanded' : undefined}
			data-testid={`${TEST_ELEMENT_IDS.MIP_MANAGE_INVESTORS_DETAILS_ACTIVE_JOURNEY}_${journey.id}`}
		>
			<div className="header">
				<Link
					to={journey.to}
					className="header-link"
					data-testid={makeComplexDataTestId(
						TEST_ELEMENT_IDS.MIP_MANAGE_INVESTORS_DETAILS_ACTIVE_JOURNEY_LINK,
						journey.kind
					)}
				>
					<span className="icon-badge">
						<Icon variant={journey.icon} size="lg" />
					</span>

					<div className="labels">
						<Text fz={14} c="gray.8" truncate>
							{journey.type}
						</Text>

						<Text fz={14} fw={600} c="white" truncate>
							{journey.descriptor}
						</Text>
					</div>
				</Link>

				{isExpandable && (
					<ActionIcon
						variant="subtle"
						size="md"
						className="toggle"
						aria-label={journey.descriptor}
						aria-expanded={expanded}
						data-testid={`${TEST_ELEMENT_IDS.MIP_MANAGE_INVESTORS_DETAILS_ACTIVE_JOURNEY_TOGGLE}_${journey.id}`}
						onClick={() => setExpanded((current) => !current)}
					>
						<Icon variant={expanded ? 'caret-up' : 'caret-down'} size="md" />
					</ActionIcon>
				)}
			</div>

			<Collapse in={expanded}>
				{journey.indicators.map((indicator) => (
					<Flex key={indicator.key} gap="0.75rem" className="indicator">
						<figure className="indicator-spacer"></figure>

						<Flex direction="column" gap="0.25rem" className="indicator-content">
							<Flex align="center" gap="0.25rem">
								<Text fz={14} c="gray.8">
									{indicator.label}
								</Text>

								<Icon variant="arrow-right" size="md" />
							</Flex>

							<div className="counts">
								{indicator.counts.map(({ status, count }) => (
									<div key={status} className="count">
										<Text c={getIndicatorColor(status)} display="flex">
											<Icon variant={getIndicatorIcon(status)} size="lg" />
										</Text>

										<Text fz={14} fw={600} c="white">
											{count}
										</Text>
									</div>
								))}
							</div>
						</Flex>
					</Flex>
				))}
			</Collapse>
		</ActiveJourneyStyles>
	);
};
